import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../utils/api';
import ConfirmModal from '../../components/modals/ConfirmModal';
import SuccessModal from '../../components/modals/SuccessModal';
import ErrorModal from '../../components/modals/ErrorModal';

const SubscriptionPackagesPage = () => {
  const { currentUser } = useOutletContext();
  
  const [packages, setPackages] = useState({});
  const [originalPackages, setOriginalPackages] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingTierNames, setEditingTierNames] = useState({});
  const [isCreatingPlans, setIsCreatingPlans] = useState(false);
  const [isUpdatingPlans, setIsUpdatingPlans] = useState(false);
  const [showPayPalInfo, setShowPayPalInfo] = useState(false);
  const [paypalConfig, setPaypalConfig] = useState(null);
  
  // Custom modal states for replacing browser dialogs
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} });
  const [successModal, setSuccessModal] = useState({ isOpen: false, title: '', message: '' });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });
  
  // Subscription notification modal state
  const [subscriptionNotifyModal, setSubscriptionNotifyModal] = useState({
    isOpen: false,
    changes: [], // Array of { tierKey, packageName, priceChanged, featuresChanged, oldPrice, newPrice, affectedCount }
    sending: false
  });
  
  // Delete notification modal state
  const [deleteNotifyModal, setDeleteNotifyModal] = useState({
    isOpen: false,
    tierKey: '',
    packageName: '',
    affectedCount: 0,
    deleting: false
  });

  // Fetch packages from backend
  useEffect(() => {
    fetchPackages();
    fetchPayPalConfig();
  }, []);

  // Helper function to check if active mode has valid credentials
  const hasActiveCredentials = () => {
    if (!paypalConfig) return false;
    const mode = paypalConfig.active_mode || 'sandbox';
    if (mode === 'live') {
      return !!(paypalConfig.live_client_id && paypalConfig.live_client_secret);
    }
    return !!(paypalConfig.sandbox_client_id && paypalConfig.sandbox_client_secret);
  };

  const fetchPackages = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/super-admin/subscription-packages`);
      const packagesData = {};
      
      response.data.forEach(pkg => {
        packagesData[pkg.tier_key] = {
          id: pkg.id,
          name: pkg.name,
          price: pkg.price,
          features: pkg.features
        };
      });
      
      setPackages(packagesData);
      setOriginalPackages(JSON.parse(JSON.stringify(packagesData))); // Deep clone
    } catch (error) {
      console.error('Error fetching packages:', error);
      setErrorModal({
        isOpen: true,
        title: 'Load Failed',
        message: 'Failed to load subscription packages'
      });
    }
    setIsLoading(false);
  };


  const fetchPayPalConfig = async () => {
    try {
      // Use new PayPal config endpoint
      const response = await axios.get(`${API}/super-admin/paypal-config`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPaypalConfig(response.data);
    } catch (error) {
      // Config doesn't exist yet - that's okay
      console.log('PayPal config not found:', error);
    }
  };

  const createPayPalPlans = async () => {
    setIsCreatingPlans(true);
    try {
      const response = await axios.post(`${API}/super-admin/paypal/create-plans`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const mode = paypalConfig?.active_mode?.toUpperCase() || 'SANDBOX';
      setSuccessModal({
        isOpen: true,
        title: 'PayPal Plans Created',
        message: `Successfully created ${response.data.created_plans.length} PayPal subscription plan(s) in ${mode} mode. ${
          response.data.errors.length > 0 
            ? `${response.data.errors.length} error(s) occurred.` 
            : ''
        }`
      });
      
      // Refresh packages to show PayPal IDs
      await fetchPackages();
      await fetchPayPalConfig();
      
    } catch (error) {
      setErrorModal({
        isOpen: true,
        title: 'Plan Creation Failed',
        message: error.response?.data?.detail || 'Failed to create PayPal subscription plans. Please check your PayPal credentials and try again.'
      });
    } finally {
      setIsCreatingPlans(false);
    }
  };

  const updatePayPalPlans = async () => {
    setIsUpdatingPlans(true);
    try {
      const response = await axios.post(`${API}/super-admin/paypal/update-plans`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const { updated_plans, unchanged_plans, errors } = response.data;
      const mode = paypalConfig?.active_mode?.toUpperCase() || 'SANDBOX';
      
      let message = `Mode: ${mode}\n\n`;
      if (updated_plans.length > 0) {
        message += `Updated ${updated_plans.length} plan(s):\n`;
        updated_plans.forEach(plan => {
          message += `\n• ${plan.name}: ${plan.changes}`;
        });
      }
      if (unchanged_plans.length > 0) {
        message += `\n${unchanged_plans.length} plan(s) unchanged.`;
      }
      if (errors.length > 0) {
        message += `\n${errors.length} error(s) occurred.`;
      }
      
      setSuccessModal({
        isOpen: true,
        title: 'PayPal Plans Updated',
        message: message || 'No updates needed - all plans match PayPal.'
      });
      
      // Refresh packages to show new PayPal IDs
      await fetchPackages();
      
    } catch (error) {
      setErrorModal({
        isOpen: true,
        title: 'Update Failed',
        message: error.response?.data?.detail || 'Failed to update PayPal plans. Please check your PayPal credentials and try again.'
      });
    } finally {
      setIsUpdatingPlans(false);
    }
  };


  const handleFeatureChange = (tierKey, featureKey, value) => {
    setPackages(prev => ({
      ...prev,
      [tierKey]: {
        ...prev[tierKey],
        features: {
          ...prev[tierKey].features,
          [featureKey]: value
        }
      }
    }));
  };

  const handlePriceChange = (tierKey, value) => {
    setPackages(prev => ({
      ...prev,
      [tierKey]: {
        ...prev[tierKey],
        price: value
      }
    }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      // Track changes for notification
      const changesForNotification = [];
      
      // Separate new packages from existing ones
      const savePromises = Object.entries(packages).map(async ([tierKey, pkg]) => {
        // Check if this is a new package (starts with "new-package-")
        if (pkg.id.startsWith('new-package-')) {
          // Create new package
          return axios.post(`${API}/super-admin/subscription-packages`, {
            name: pkg.name,
            tier_key: tierKey,
            price: pkg.price,
            features: pkg.features,
            active: true
          });
        } else {
          // Update existing package
          const response = await axios.put(`${API}/super-admin/subscription-packages/${pkg.id}`, {
            id: pkg.id,
            name: pkg.name,
            tier_key: tierKey,
            price: pkg.price,
            features: pkg.features,
            active: true
          });
          
          // Track if this package had meaningful changes
          if (response.data.changes_detected && (response.data.price_changed || response.data.features_changed)) {
            changesForNotification.push({
              tierKey: response.data.tier_key,
              packageName: response.data.package_name,
              priceChanged: response.data.price_changed,
              featuresChanged: response.data.features_changed,
              oldPrice: response.data.old_price,
              newPrice: response.data.new_price,
              affectedCount: response.data.affected_admins_count
            });
          }
          
          return response;
        }
      });

      await Promise.all(savePromises);
      
      // If there were changes that affect users, show notification modal
      if (changesForNotification.length > 0) {
        setSubscriptionNotifyModal({
          isOpen: true,
          changes: changesForNotification,
          sending: false
        });
      } else {
        setSuccessModal({
          isOpen: true,
          title: 'Saved Successfully',
          message: 'Subscription packages saved successfully!'
        });
      }
      
      // Refresh packages from backend to get proper IDs for new packages
      await fetchPackages();
      
      // Clear editing states
      setEditingTierNames({});
    } catch (error) {
      console.error('Error saving packages:', error);
      setErrorModal({
        isOpen: true,
        title: 'Save Failed',
        message: 'Failed to save changes. Please try again.'
      });
    }
    setIsSaving(false);
  };

  const handleResetChanges = () => {
    setPackages(JSON.parse(JSON.stringify(originalPackages)));
  };

  const handleDeletePackage = async (tierKey, packageName) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Subscription Package',
      message: `Are you sure you want to delete the "${packageName}" subscription package?\n\nThis action cannot be undone.`,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} });
        
        // Show notification choice modal
        setDeleteNotifyModal({
          isOpen: true,
          tierKey: tierKey,
          packageName: packageName,
          packageId: packages[tierKey].id,
          deleting: false
        });
      },
      onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} })
    });
  };

  // Handle delete with notification choice
  const handleDeleteWithNotify = async (notify) => {
    const { tierKey, packageName, packageId } = deleteNotifyModal;
    setDeleteNotifyModal(prev => ({ ...prev, deleting: true }));
    
    try {
      const url = `${API}/super-admin/subscription-packages/${packageId}${notify ? '?notify_admins=true' : ''}`;
      const response = await axios.delete(url);
      
      let message = `${packageName} package deleted successfully!`;
      
      if (notify && response.data.notifications) {
        const { sent, failed } = response.data.notifications;
        if (sent > 0) {
          message += `\n\n📧 ${sent} admin(s) notified`;
        }
        if (failed > 0) {
          message += `\n⚠️ ${failed} notification(s) could not be sent`;
        }
      }
      
      setSuccessModal({
        isOpen: true,
        title: 'Deleted Successfully',
        message: message
      });
      
      await fetchPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
      setErrorModal({
        isOpen: true,
        title: 'Delete Failed',
        message: 'Failed to delete package. Please try again.'
      });
    } finally {
      setDeleteNotifyModal({ isOpen: false, tierKey: '', packageName: '', packageId: '', deleting: false });
    }
  };

  // Handle sending subscription change notifications
  const handleSendSubscriptionNotifications = async () => {
    setSubscriptionNotifyModal(prev => ({ ...prev, sending: true }));
    
    try {
      const notifyPromises = subscriptionNotifyModal.changes.map(change => {
        const changeType = change.priceChanged ? 'pricing_updated' : 'features_updated';
        
        return axios.post(`${API}/super-admin/subscription-packages/notify`, {
          tier_key: change.tierKey,
          package_name: change.packageName,
          change_type: changeType,
          old_price: change.oldPrice,
          new_price: change.newPrice,
          changes: change.featuresChanged ? ['Feature configuration has been updated'] : null
        });
      });
      
      const responses = await Promise.all(notifyPromises);
      const totalEmails = responses.reduce((sum, r) => sum + (r.data.total_emails || 0), 0);
      
      setSuccessModal({
        isOpen: true,
        title: 'Notifications Sent',
        message: `✅ Changes saved successfully!\n\n📧 ${totalEmails} notification email(s) are being sent.`
      });
    } catch (error) {
      console.error('Error sending notifications:', error);
      setSuccessModal({
        isOpen: true,
        title: 'Saved with Warning',
        message: '✅ Changes saved successfully.\n\n⚠️ Failed to send some notifications.'
      });
    } finally {
      setSubscriptionNotifyModal({ isOpen: false, changes: [], sending: false });
    }
  };

  // Handle skipping notifications
  const handleSkipSubscriptionNotifications = () => {
    setSubscriptionNotifyModal({ isOpen: false, changes: [], sending: false });
    setSuccessModal({
      isOpen: true,
      title: 'Saved Successfully',
      message: 'Subscription packages saved successfully! No notifications sent.'
    });
  };

  const handleCreateNewPackage = () => {
    // Generate a unique key for the new package
    const newTierKey = `custom_${Date.now()}`;
    
    // Create a new package with default values
    const newPackage = {
      id: `new-package-${Date.now()}`,
      name: 'New Package',
      price: '£0',
      features: {
        dashboard: false,
        siteManagement: false,
        maxSites: '0',
        procurement: false,
        teamManagement: false,
        maxTeamMembers: '0',
        storage: '0',
        taskManagement: false,
        photoGallery: false,
        teamCapacity: false,
        teamPerformance: false,
        expenses: false,
        overtime: false,
        payments: false,
        workDiary: false,
        progressReports: false,
        holidaysModule: false,
        adminMobileApp: false,
        offlineMode: false,
        pushNotifications: false,
        autoClockOut: false,
        neo: false,
        aiReportGeneration: false,
        aiTasksScheduler: false,
        neoCreditsMonthly: '0',
        neoCreditsMonthlyTeam: '0'
      }
    };
    
    setPackages(prev => ({
      ...prev,
      [newTierKey]: newPackage
    }));
    
    // Enable editing for the new tier name
    setEditingTierNames(prev => ({
      ...prev,
      [newTierKey]: true
    }));
  };

  const handleTierNameChange = (tierKey, newName) => {
    setPackages(prev => ({
      ...prev,
      [tierKey]: {
        ...prev[tierKey],
        name: newName
      }
    }));
  };

  const toggleTierNameEdit = (tierKey) => {
    setEditingTierNames(prev => ({
      ...prev,
      [tierKey]: !prev[tierKey]
    }));
  };

  const handleMovePackageLeft = (tierKey) => {
    const packageKeys = Object.keys(packages);
    const currentIndex = packageKeys.indexOf(tierKey);
    
    if (currentIndex <= 0) return; // Already at leftmost position
    
    // Swap with previous package
    const newPackages = {};
    packageKeys.forEach((key, index) => {
      if (index === currentIndex - 1) {
        newPackages[packageKeys[currentIndex]] = packages[packageKeys[currentIndex]];
      } else if (index === currentIndex) {
        newPackages[packageKeys[currentIndex - 1]] = packages[packageKeys[currentIndex - 1]];
      } else {
        newPackages[key] = packages[key];
      }
    });
    
    setPackages(newPackages);
  };

  const handleMovePackageRight = (tierKey) => {
    const packageKeys = Object.keys(packages);
    const currentIndex = packageKeys.indexOf(tierKey);
    
    if (currentIndex >= packageKeys.length - 1) return; // Already at rightmost position
    
    // Swap with next package
    const newPackages = {};
    packageKeys.forEach((key, index) => {
      if (index === currentIndex) {
        newPackages[packageKeys[currentIndex + 1]] = packages[packageKeys[currentIndex + 1]];
      } else if (index === currentIndex + 1) {
        newPackages[packageKeys[currentIndex]] = packages[packageKeys[currentIndex]];
      } else {
        newPackages[key] = packages[key];
      }
    });
    
    setPackages(newPackages);
  };

  // Define original state structure for backwards compatibility
  const [, ] = useState({
    starter: {
      name: 'Starter',
      price: '£49',
      features: {
        dashboard: true,
        siteManagement: true,
        maxSites: '3',
        procurement: false,
        teamManagement: true,
        maxTeamMembers: '10',
        storage: '5',
        taskManagement: true,
        photoGallery: true,
        teamCapacity: false,
        teamPerformance: false,
        expenses: true,
        overtime: true,
        payments: false,
        workDiary: true,
        progressReports: false,
        quotesAndInvoices: false,
        holidaysModule: false,
        adminMobileApp: false,
        offlineMode: false,
        pushNotifications: false,
        autoClockOut: true,
        neo: false,
        aiReportGeneration: false,
        aiTasksScheduler: false,
        neoCreditsMonthly: '0',
        neoCreditsMonthlyTeam: '0'
      }
    },
    business: {
      name: 'Business',
      price: '£99',
      features: {
        dashboard: true,
        siteManagement: true,
        maxSites: '10',
        procurement: true,
        teamManagement: true,
        maxTeamMembers: '50',
        storage: '25',
        taskManagement: true,
        photoGallery: true,
        teamCapacity: true,
        teamPerformance: true,
        expenses: true,
        overtime: true,
        payments: true,
        workDiary: true,
        progressReports: true,
        quotesAndInvoices: true,
        holidaysModule: true,
        adminMobileApp: true,
        offlineMode: true,
        pushNotifications: true,
        autoClockOut: true,
        neo: true,
        aiReportGeneration: false,
        aiTasksScheduler: false,
        neoCreditsMonthly: '100',
        neoCreditsMonthlyTeam: '50'
      }
    },
    pro: {
      name: 'Pro',
      price: '£199',
      features: {
        dashboard: true,
        siteManagement: true,
        maxSites: 'Unlimited',
        procurement: true,
        teamManagement: true,
        maxTeamMembers: 'Unlimited',
        storage: '100',
        taskManagement: true,
        photoGallery: true,
        teamCapacity: true,
        teamPerformance: true,
        expenses: true,
        overtime: true,
        payments: true,
        workDiary: true,
        progressReports: true,
        quotesAndInvoices: true,
        holidaysModule: true,
        adminMobileApp: true,
        offlineMode: true,
        pushNotifications: true,
        autoClockOut: true,
        neo: true,
        aiReportGeneration: true,
        aiTasksScheduler: true,
        neoCreditsMonthly: '500',
        neoCreditsMonthlyTeam: '100'
      }
    }
  });

  const featuresList = [
    // Group 1: Pricing
    { key: 'price', label: 'Price', type: 'price', group: 'Pricing' },
    
    // Group 2: Organisation Setup
    { key: 'maxSites', label: 'Max. Number of Sites', type: 'number', group: 'Organisation Setup', tooltip: 'Enter a number (e.g., 5, 10, 50) or type "Unlimited" for no limit' },
    { key: 'maxTeamMembers', label: 'Max. Number of Team Members', type: 'number', group: 'Organisation Setup', tooltip: 'Enter a number (e.g., 10, 50, 100) or type "Unlimited" for no limit' },
    { key: 'storage', label: 'Storage (GB)', type: 'decimal', group: 'Organisation Setup', tooltip: 'Enter storage limit in GB (e.g., 5, 10.5, 100)' },
    
    // Group 3: Core Modules
    { key: 'dashboard', label: 'Dashboard with comprehensive metrics', type: 'boolean', group: 'Core Modules' },
    { key: 'siteManagement', label: 'Site Management', type: 'boolean', group: 'Core Modules' },
    { key: 'teamManagement', label: 'Team Management', type: 'boolean', group: 'Core Modules' },
    { key: 'taskManagement', label: 'Task Management', type: 'boolean', group: 'Core Modules' },
    { key: 'photoGallery', label: 'Photo Gallery', type: 'boolean', group: 'Core Modules' },
    
    // Group 4: Additional Modules
    { key: 'procurement', label: 'Procurement (Materials)', type: 'boolean', group: 'Additional Modules' },
    { key: 'expenses', label: 'Expenses', type: 'boolean', group: 'Additional Modules' },
    { key: 'overtime', label: 'Overtime', type: 'boolean', group: 'Additional Modules' },
    { key: 'holidaysModule', label: 'Holidays', type: 'boolean', group: 'Additional Modules' },
    
    // Group 5: Business Features
    { key: 'teamCapacity', label: 'Team Capacity', type: 'boolean', group: 'Business Features' },
    { key: 'teamPerformance', label: 'Team Performance', type: 'boolean', group: 'Business Features' },
    { key: 'workDiary', label: 'Work Diary', type: 'boolean', group: 'Business Features' },
    { key: 'progressReports', label: 'Progress Reports', type: 'boolean', group: 'Business Features' },
    { key: 'quotesAndInvoices', label: 'Quotes & Invoices', type: 'boolean', group: 'Business Features' },
    { key: 'payments', label: 'Payments & CIS Statements', type: 'boolean', group: 'Business Features' },
    
    // Group 6: Mobile App Experience
    { key: 'adminMobileApp', label: 'Admin Mobile App', type: 'boolean', group: 'Mobile App Experience' },
    { key: 'offlineMode', label: 'Offline Clock-in / Clock-out', type: 'boolean', group: 'Mobile App Experience' },
    { key: 'autoClockOut', label: 'Auto Clock-Out', type: 'boolean', group: 'Mobile App Experience' },
    { key: 'pushNotifications', label: 'Push Notifications', type: 'boolean', group: 'Mobile App Experience' },
    
    // Group 7: AI Powered Features
    { key: 'neo', label: 'Neo AI', type: 'boolean', group: 'AI Powered Features' },
    { key: 'aiReportGeneration', label: 'AI Report Generation', type: 'boolean', group: 'AI Powered Features' },
    { key: 'neoCreditsMonthly', label: 'Neo Credits (Monthly) - Admin', type: 'decimal', group: 'AI Powered Features', tooltip: 'Enter monthly Neo AI credits allocation for Admin users (e.g., 100, 500, 1000)' },
    { key: 'neoCreditsMonthlyTeam', label: 'Neo Credits (Monthly) - Team', type: 'decimal', group: 'AI Powered Features', tooltip: 'Enter monthly Neo AI credits allocation for Team Members (e.g., 50, 100, 250)' }
  ];

  const renderFeatureValue = (tierKey, feature) => {
    if (!packages[tierKey]) return null;
    
    if (feature.type === 'price') {
      return (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">£</span>
          <input
            type="text"
            value={packages[tierKey].price.replace('£', '')}
            onChange={(e) => {
              const value = e.target.value.replace(/[^\d.]/g, '');
              // Prevent multiple decimal points
              const parts = value.split('.');
              const sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
              handlePriceChange(tierKey, sanitized ? `£${sanitized}` : '£0');
            }}
            className="w-28 pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-center font-medium text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors bg-white"
            placeholder="0.00"
          />
        </div>
      );
    } else if (feature.type === 'number') {
      const value = packages[tierKey].features[feature.key];
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => handleFeatureChange(tierKey, feature.key, e.target.value)}
          className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-center font-medium text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors bg-white"
        />
      );
    } else if (feature.type === 'decimal') {
      const value = packages[tierKey].features[feature.key] || '0';
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => {
            // Allow only numbers and a single decimal point
            const inputValue = e.target.value.replace(/[^\d.]/g, '');
            // Prevent multiple decimal points
            const parts = inputValue.split('.');
            const sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : inputValue;
            handleFeatureChange(tierKey, feature.key, sanitized);
          }}
          className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-center font-medium text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors bg-white"
          placeholder="0"
        />
      );
    } else {
      const isEnabled = packages[tierKey].features[feature.key];
      return (
        <button
          onClick={() => handleFeatureChange(tierKey, feature.key, !isEnabled)}
          className="inline-flex items-center justify-center hover:scale-110 transition-transform"
          title={isEnabled ? "Click to disable" : "Click to enable"}
        >
          {isEnabled ? (
            <svg className="w-7 h-7 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-7 h-7 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}
        </button>
      );
    }
  };

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600 text-lg">Loading subscription packages...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header - Matching Theme */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white mb-8 shadow-xl">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div>
            <h1 className="text-3xl font-bold">Subscription Packages</h1>
            <p className="text-purple-200 text-sm">Manage subscription tiers and pricing for organisations</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 mb-6">
        {/* Create PayPal Plans Button - only show if active mode has credentials */}
        {hasActiveCredentials() && (
          <button 
            onClick={() => {
              const mode = paypalConfig.active_mode?.toUpperCase() || 'SANDBOX';
              const vatRate = paypalConfig.vat_rate ?? 20;
              setConfirmModal({
                isOpen: true,
                title: `Create PayPal Plans (${mode})`,
                message: `This will create subscription plans in PayPal ${mode} mode for all your packages.\n\n• VAT: ${vatRate}% (inclusive in prices)\n• Billing starts immediately upon subscription\n• Trial/grace periods are managed locally by SiteOrbit\n\nThis action is safe to run multiple times (existing plans will be skipped). Do you want to proceed?`,
                onConfirm: () => {
                  setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} });
                  createPayPalPlans();
                },
                onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} })
              });
            }}
            disabled={isCreatingPlans || isSaving}
            className={`px-6 py-3 rounded-lg font-semibold transition-all shadow-lg flex items-center ${
              isCreatingPlans || isSaving
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl'
            }`}
            data-testid="create-paypal-plans-btn"
          >
            {isCreatingPlans ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Plans...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                  <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                </svg>
                Create PayPal Plans
              </>
            )}
          </button>
        )}

        
        {hasActiveCredentials() && paypalConfig.plans_created && (
          <button 
            onClick={() => {
              const mode = paypalConfig.active_mode?.toUpperCase() || 'SANDBOX';
              const vatRate = paypalConfig.vat_rate ?? 20;
              setConfirmModal({
                isOpen: true,
                title: `Update PayPal Plans (${mode})`,
                message: `This will check for changes in PayPal ${mode} mode:\n\n• Price changes: Updated in-place\n• Name/description changes: Updated in-place\n• VAT rate: ${vatRate}% (inclusive)\n\nExisting subscribers remain on their current terms. Do you want to proceed?`,
                onConfirm: () => {
                  setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} });
                  updatePayPalPlans();
                },
                onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, onClose: () => {} })
              });
            }}
            disabled={isUpdatingPlans || isSaving}
            className={`px-6 py-3 rounded-lg font-semibold transition-all shadow-lg flex items-center ${
              isUpdatingPlans || isSaving
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 hover:shadow-xl'
            }`}
            data-testid="update-paypal-plans-btn"
          >
            {isUpdatingPlans ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating Plans...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Update PayPal Plans
              </>
            )}
          </button>
        )}

        <button 
          onClick={handleCreateNewPackage}
          disabled={isSaving}
          className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          + Create New Package
        </button>
      </div>

      {/* PayPal Mode Indicator */}
      {hasActiveCredentials() && (
        <div className={`mb-4 px-4 py-2 rounded-lg border-2 inline-flex items-center ${
          paypalConfig.active_mode === 'live' 
            ? 'bg-green-50 border-green-400 text-green-800' 
            : 'bg-amber-50 border-amber-400 text-amber-800'
        }`}>
          <div className={`w-2 h-2 rounded-full mr-2 ${
            paypalConfig.active_mode === 'live' ? 'bg-green-500' : 'bg-amber-500'
          } animate-pulse`}></div>
          <span className="text-sm font-medium">
            PayPal Mode: <strong>{paypalConfig.active_mode?.toUpperCase() || 'SANDBOX'}</strong>
            {paypalConfig.currency && <span className="ml-2">| Currency: <strong>{paypalConfig.currency}</strong></span>}
          </span>
        </div>
      )}

      {/* PayPal Buttons Info - Collapsible */}
      {hasActiveCredentials() && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg mb-8">
          <button
            onClick={() => setShowPayPalInfo(!showPayPalInfo)}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-blue-100 transition-colors rounded-lg"
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-semibold text-blue-900">PayPal Buttons Guide</span>
            </div>
            <svg className={`w-5 h-5 text-blue-600 transform transition-transform ${showPayPalInfo ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showPayPalInfo && (
            <div className="px-4 pb-4 space-y-3 text-sm text-blue-900">
              <div className="border-l-4 border-blue-400 pl-3 py-1">
                <strong className="text-blue-800">Create PayPal Plans:</strong> Initial setup to register your subscription packages with PayPal. Run once when adding new tiers. Skips existing plans.
              </div>
              <div className="border-l-4 border-green-400 pl-3 py-1">
                <strong className="text-green-800">Update PayPal Plans:</strong> Updates pricing on existing PayPal plans when you change prices in SiteOrbit. Uses PayPal&apos;s pricing update API to modify active plans in-place.
              </div>
              <div className="bg-white border border-blue-200 rounded p-2 text-xs text-gray-700">
                <strong>Note:</strong> Price updates apply to the plan itself. Existing subscribers will see new pricing on their next billing cycle. PayPal handles the transition automatically.
              </div>
            </div>
          )}
        </div>
      )}

      {/* PayPal Configuration Notice */}
      {!hasActiveCredentials() ? (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-8">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-bold text-sm text-amber-900 mb-1">PayPal Not Configured</p>
              <p className="text-amber-800 text-sm">
                {paypalConfig ? (
                  <>No credentials configured for <strong>{paypalConfig.active_mode?.toUpperCase() || 'SANDBOX'}</strong> mode. Please add credentials in <strong>PayPal Setup</strong>.</>
                ) : (
                  <>Please configure PayPal credentials in <strong>PayPal Setup</strong> before creating subscription plans.</>
                )}
              </p>
            </div>
          </div>
        </div>
      ) : paypalConfig.plans_created ? (
        <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-8">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-bold text-sm text-green-900 mb-1">PayPal Plans Synced</p>
              <p className="text-green-800 text-sm">
                Your subscription packages are synced with PayPal in <strong>{paypalConfig.active_mode?.toUpperCase() || 'SANDBOX'}</strong> mode. Currency: <strong>{paypalConfig.currency}</strong>
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Comparison Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            {/* Table Header */}
            <thead>
              <tr className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900">
                <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider border-r border-gray-700">
                  Features
                </th>
                {Object.entries(packages).map(([tierKey, pkg], index) => (
                  <th 
                    key={tierKey} 
                    className={`px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider ${
                      index < Object.keys(packages).length - 1 ? 'border-r border-gray-700' : ''
                    }`}
                  >
                    {editingTierNames[tierKey] ? (
                      <input
                        type="text"
                        value={pkg.name}
                        onChange={(e) => handleTierNameChange(tierKey, e.target.value)}
                        onBlur={() => toggleTierNameEdit(tierKey)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            toggleTierNameEdit(tierKey);
                          }
                        }}
                        className="bg-white/20 text-white text-center px-2 py-1 rounded border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
                        autoFocus
                      />
                    ) : (
                      <div 
                        onClick={() => toggleTierNameEdit(tierKey)}
                        className="cursor-pointer hover:bg-white/10 rounded px-2 py-1 transition-colors inline-flex items-center"
                        title="Click to edit"
                      >
                        {pkg.name}
                        <svg className="w-3 h-3 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="divide-y divide-gray-200">
              {(() => {
                let currentGroup = '';
                const rows = [];
                
                featuresList.forEach((feature, index) => {
                  // Add group header row if group changed
                  if (feature.group && feature.group !== currentGroup) {
                    currentGroup = feature.group;
                    rows.push(
                      <tr key={`group-${currentGroup}`} className="bg-gradient-to-r from-indigo-100 to-purple-100">
                        <td 
                          colSpan={Object.keys(packages).length + 1} 
                          className="px-6 py-3 text-sm font-bold text-gray-900 uppercase tracking-wide"
                        >
                          {currentGroup}
                        </td>
                      </tr>
                    );
                  }
                  
                  // Add feature row
                  rows.push(
                    <tr key={feature.key} className={index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                        <div className="flex items-center">
                          <span>{feature.label}</span>
                          {feature.tooltip && (
                            <div className="group relative ml-2">
                              <svg className="w-4 h-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                                {feature.tooltip}
                                <div className="absolute left-4 top-full w-2 h-2 bg-gray-900 transform rotate-45 -mt-1"></div>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      {Object.entries(packages).map(([tierKey], colIndex) => (
                        <td 
                          key={tierKey} 
                          className={`px-6 py-4 ${
                            colIndex < Object.keys(packages).length - 1 ? 'border-r border-gray-200' : ''
                          }`}
                        >
                          <div className="flex items-center justify-center">
                            {renderFeatureValue(tierKey, feature)}
                          </div>
                        </td>
                      ))}
                    </tr>
                  );
                });
                
                return rows;
              })()}
              
              {/* Actions Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300">
                <td className="px-6 py-4 text-sm font-semibold text-gray-900 border-r border-gray-200">
                  Actions
                </td>
                {Object.entries(packages).map(([tierKey, pkg], colIndex) => {
                  const packageKeys = Object.keys(packages);
                  const isFirst = colIndex === 0;
                  const isLast = colIndex === packageKeys.length - 1;
                  
                  return (
                    <td 
                      key={tierKey}
                      className={`px-6 py-4 ${
                        colIndex < Object.keys(packages).length - 1 ? 'border-r border-gray-200' : ''
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        {/* Move Left Button */}
                        <button
                          onClick={() => handleMovePackageLeft(tierKey)}
                          disabled={isSaving || isFirst}
                          className="inline-flex items-center justify-center p-2 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-600 hover:text-purple-700 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-purple-100"
                          title="Move Left"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        
                        {/* Move Right Button */}
                        <button
                          onClick={() => handleMovePackageRight(tierKey)}
                          disabled={isSaving || isLast}
                          className="inline-flex items-center justify-center p-2 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-600 hover:text-purple-700 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-purple-100"
                          title="Move Right"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        
                        {/* Delete Button */}
                        <button
                          onClick={() => handleDeletePackage(tierKey, pkg.name)}
                          disabled={isSaving}
                          className="inline-flex items-center justify-center p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={`Delete ${pkg.name} Package`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex justify-end space-x-4">
        <button 
          onClick={handleResetChanges}
          className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors"
          disabled={isSaving}
        >
          Reset Changes
        </button>
        <button 
          onClick={handleSaveChanges}
          disabled={isSaving}
          className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {isSaving ? (
            <>
              <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>

      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onClose={confirmModal.onClose}
      />
      
      <SuccessModal
        isOpen={successModal.isOpen}
        title={successModal.title}
        message={successModal.message}
        onClose={() => setSuccessModal({ isOpen: false, title: '', message: '' })}
      />
      
      <ErrorModal
        isOpen={errorModal.isOpen}
        title={errorModal.title}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, title: '', message: '' })}
      />

      {/* Subscription Change Notification Modal */}
      {subscriptionNotifyModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold">Changes Saved Successfully!</h3>
                  <p className="text-sm opacity-90">Would you like to notify affected organisations?</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <p className="text-sm text-gray-600 mb-4">
                The following subscription packages were updated. You can notify all affected organisations via email.
              </p>

              {/* Changes Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Package</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Changes</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Affected Orgs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptionNotifyModal.changes.map((change, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 font-medium text-gray-700">{change.packageName}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-col gap-1">
                            {change.priceChanged && (
                              <span className="inline-flex items-center gap-1 text-purple-600">
                                <span className="text-gray-400 line-through">£{change.oldPrice}</span>
                                <span>→</span>
                                <span className="font-medium">£{change.newPrice}</span>
                              </span>
                            )}
                            {change.featuresChanged && (
                              <span className="text-cyan-600 text-xs">Features updated</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-600">{change.affectedCount} organisation(s)</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>{subscriptionNotifyModal.changes.reduce((sum, c) => sum + c.affectedCount, 0)}</strong> admin(s) will receive email notifications about these changes.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleSkipSubscriptionNotifications}
                  disabled={subscriptionNotifyModal.sending}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium disabled:opacity-50"
                >
                  Skip Notifications
                </button>
                <button
                  onClick={handleSendSubscriptionNotifications}
                  disabled={subscriptionNotifyModal.sending}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg hover:from-purple-600 hover:to-indigo-700 transition-all font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {subscriptionNotifyModal.sending ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Notify All Admins
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Package Notification Modal */}
      {deleteNotifyModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-red-500 to-rose-600 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold">Notify Admins Before Deletion?</h3>
                  <p className="text-sm opacity-90">Choose notification preference</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Package:</strong> {deleteNotifyModal.packageName}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Tier:</strong> {deleteNotifyModal.tierKey}
                </p>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Would you like to notify all organisations currently on this subscription tier before deleting the package?
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Note:</strong> Affected organisations will need to be migrated to a different plan. Consider notifying them first.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleDeleteWithNotify(true)}
                  disabled={deleteNotifyModal.deleting}
                  className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg hover:from-blue-600 hover:to-cyan-700 transition-all font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleteNotifyModal.deleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Notify Admins & Delete
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleDeleteWithNotify(false)}
                  disabled={deleteNotifyModal.deleting}
                  className="w-full px-4 py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-lg hover:from-red-600 hover:to-rose-700 transition-all font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleteNotifyModal.deleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Without Notifying
                    </>
                  )}
                </button>
                <button
                  onClick={() => setDeleteNotifyModal({ isOpen: false, tierKey: '', packageName: '', packageId: '', deleting: false })}
                  disabled={deleteNotifyModal.deleting}
                  className="w-full px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPackagesPage;